import type { AiPracticeArea } from '@portfolio/shared';

/**
 * The AI thread across the career, gathered in one place.
 *
 * Every point below restates something already recorded in `experience.ts`,
 * `caseStudies.ts` or `projects.ts` — this module adds structure, not claims.
 * Each area names where the work was done so it can be checked.
 */
export const aiIntro =
  'AI work has been the centre of my role since 2025: not model training, but the engineering ' +
  'around it — the interfaces that let people configure a model-driven job, watch it run, and ' +
  'decide whether to trust what it produced.';

export const aiPracticeAreas: readonly AiPracticeArea[] = [
  {
    id: 'workflows',
    title: 'LLM-driven workflows people can operate',
    description:
      'An AI pipeline is only useful if someone can set it up, start it and follow it. That ' +
      'configure–execute–monitor loop is the work I owned at IBM.',
    points: [
      'Source-code ingestion and project configuration',
      'Migration job creation and execution',
      'Monitoring a transformation while it runs',
    ],
    evidenceLabel: 'IBM case study',
    evidenceHref: '/case-studies/ibm-ai-code-transformation-platform',
  },
  {
    id: 'async',
    title: 'Asynchronous AI jobs as first-class UI state',
    description:
      'Model-driven jobs run long and fail in more than one way. Progress, errors, retries and ' +
      'validation failures are modelled as expected states rather than error handling bolted on ' +
      'at the end.',
    points: [
      'Explicit progress reporting during execution',
      'Retry and error paths surfaced in the interface',
      'Validation failures presented as reviewable results',
    ],
    evidenceLabel: 'IBM case study',
    evidenceHref: '/case-studies/ibm-ai-code-transformation-platform',
  },
  {
    id: 'review',
    title: 'Making generated output reviewable',
    description:
      'The hardest screens in an AI product are the ones where a person decides whether the ' +
      'output is good enough. I built the review surfaces for exactly that decision.',
    points: ['Transformation review interfaces', 'Validation results', 'Generated-code analysis'],
    evidenceLabel: 'IBM case study',
    evidenceHref: '/case-studies/ibm-ai-code-transformation-platform',
  },
  {
    id: 'observability',
    title: 'Cost and confidence made visible',
    description:
      'Teams running LLM workloads need to see what a run is costing and how sure the system is, ' +
      'not just whether it finished.',
    points: ['Token usage dashboards', 'Confidence scores', 'Migration progress reporting'],
    evidenceLabel: 'IBM case study',
    evidenceHref: '/case-studies/ibm-ai-code-transformation-platform',
  },
  {
    id: 'integration',
    title: 'Integrating AI orchestration services',
    description:
      'The frontend sits on top of a Python orchestration layer and several data platforms, and ' +
      'has to surface what they report without leaking their internals into the interface.',
    points: [
      'React and Next.js integrated with Python/FastAPI over REST',
      'Code parsing, LLM execution, validation and reporting',
      'Execution metadata from MCP, Snowflake and Databricks pipelines',
    ],
    evidenceLabel: 'IBM case study',
    evidenceHref: '/case-studies/ibm-ai-code-transformation-platform',
  },
  {
    id: 'prompting',
    title: 'Prompt engineering and persona design',
    description:
      'Built independently: a platform where configurable personas, tone and conversation context ' +
      'drive what the model produces, with response quality as the product problem.',
    points: [
      'Persona creation, configuration and management',
      'Replies generated from context, tone and persona characteristics',
      'Prompt engineering and response quality as the main focus',
    ],
    evidenceLabel: 'personareply.com',
    evidenceHref: 'https://personareply.com/',
    evidenceExternal: true,
  },
];
